import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import SeverityBadge from "@/components/SeverityBadge";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";

const STATUSES = ["all", "open", "investigating", "contained", "resolved"];

export default function Incidents() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [severity, setSeverity] = useState("all");
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", severity: "medium", asset: "" });

  const load = async () => {
    const { data } = await api.get("/incidents", {
      params: {
        status: status === "all" ? undefined : status,
        severity: severity === "all" ? undefined : severity,
      },
    });
    setItems(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, severity]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/incidents", form);
      toast.success("Incident created");
      setShowNew(false);
      setForm({ title: "", description: "", severity: "medium", asset: "" });
      load();
    } catch (err) {
      toast.error("Could not create incident");
    }
  };

  return (
    <div className="p-8 max-w-[1400px] mx-auto">
      <header className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196]">Case management</div>
          <h1 className="font-display font-black text-5xl mt-2">Incidents</h1>
        </div>
        <button
          onClick={() => setShowNew(true)}
          data-testid="new-incident-btn"
          className="inline-flex items-center gap-2 border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black transition-colors px-3 py-2 rounded-sm font-mono uppercase text-xs tracking-widest"
        >
          <Plus className="w-3.5 h-3.5" /> New incident
        </button>
      </header>

      <div className="flex flex-wrap gap-3 mb-5">
        <div className="flex items-center border border-white/15 rounded-sm overflow-hidden">
          {STATUSES.map((s) => (
            <button key={s} onClick={() => setStatus(s)}
              data-testid={`filter-status-${s}`}
              className={`px-3 py-2 font-mono uppercase text-[11px] tracking-widest ${
                status === s ? "bg-white/10 text-white" : "text-[#8E9196] hover:text-white"
              }`}>{s}</button>
          ))}
        </div>
        <div className="flex items-center border border-white/15 rounded-sm overflow-hidden">
          {["all", "critical", "high", "medium", "low"].map((s) => (
            <button key={s} onClick={() => setSeverity(s)}
              data-testid={`filter-sev-${s}`}
              className={`px-3 py-2 font-mono uppercase text-[11px] tracking-widest ${
                severity === s ? "bg-white/10 text-white" : "text-[#8E9196] hover:text-white"
              }`}>{s}</button>
          ))}
        </div>
      </div>

      <div className="border border-white/10 bg-[#121212] rounded-sm overflow-hidden" data-testid="incidents-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">
              <th className="text-left px-4 py-3">ID</th>
              <th className="text-left px-4 py-3">Severity</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Title</th>
              <th className="text-left px-4 py-3">Asset</th>
              <th className="text-left px-4 py-3">Assignee</th>
              <th className="text-right px-4 py-3">Updated</th>
            </tr>
          </thead>
          <tbody>
            {items.map((inc) => (
              <tr key={inc.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                <td className="px-4 py-3 font-mono text-xs text-[#8E9196]">{inc.id.slice(0, 8)}</td>
                <td className="px-4 py-3"><SeverityBadge severity={inc.severity} /></td>
                <td className="px-4 py-3 font-mono text-[11px] uppercase tracking-widest text-[#8E9196]">{inc.status}</td>
                <td className="px-4 py-3">
                  <Link to={`/incidents/${inc.id}`} className="hover:text-[#00F0FF]" data-testid={`incident-row-${inc.id}`}>{inc.title}</Link>
                </td>
                <td className="px-4 py-3 font-mono text-xs">{inc.asset || "-"}</td>
                <td className="px-4 py-3 text-xs text-[#8E9196]">{inc.assignee || "unassigned"}</td>
                <td className="px-4 py-3 text-right font-mono text-[11px] text-[#8E9196]">{new Date(inc.updated_at).toLocaleString()}</td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={7} className="text-center text-[#52525B] py-10 text-sm">No incidents match.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create modal */}
      {showNew && (
        <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setShowNew(false)}>
          <form onSubmit={create} onClick={(e) => e.stopPropagation()}
            className="bg-[#121212] border border-white/15 rounded-sm w-full max-w-lg p-6"
            data-testid="new-incident-modal">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-2xl">New incident</div>
              <button type="button" onClick={() => setShowNew(false)} className="text-[#8E9196] hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-[11px] font-mono uppercase tracking-widest text-[#8E9196] mb-1">Title</label>
                <input required data-testid="new-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full bg-transparent border border-white/15 px-3 py-2 rounded-sm font-mono text-sm outline-none focus:border-[#00F0FF]" />
              </div>
              <div>
                <label className="block text-[11px] font-mono uppercase tracking-widest text-[#8E9196] mb-1">Description</label>
                <textarea required rows={3} data-testid="new-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full bg-transparent border border-white/15 px-3 py-2 rounded-sm font-mono text-sm outline-none focus:border-[#00F0FF]" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-mono uppercase tracking-widest text-[#8E9196] mb-1">Severity</label>
                  <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}
                    data-testid="new-severity"
                    className="w-full bg-[#0a0a0a] border border-white/15 px-3 py-2 rounded-sm font-mono text-sm outline-none focus:border-[#00F0FF]">
                    {["critical", "high", "medium", "low"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-mono uppercase tracking-widest text-[#8E9196] mb-1">Asset</label>
                  <input data-testid="new-asset" value={form.asset} onChange={(e) => setForm({ ...form, asset: e.target.value })}
                    className="w-full bg-transparent border border-white/15 px-3 py-2 rounded-sm font-mono text-sm outline-none focus:border-[#00F0FF]" />
                </div>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setShowNew(false)} className="border border-white/15 px-3 py-2 rounded-sm font-mono uppercase text-xs tracking-widest text-[#8E9196] hover:text-white">Cancel</button>
              <button data-testid="submit-new-incident" className="border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black px-3 py-2 rounded-sm font-mono uppercase text-xs tracking-widest">Create</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
